import React, { useState, useRef, useEffect } from 'react';
import { FiInfo } from 'react-icons/fi';
import TooltipContent from './TooltipContent';

interface InfoButtonProps {
    tooltipId: string;
    tooltipContent: string;
    direction?: 'left' | 'right' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

const InfoButton: React.FC<InfoButtonProps> = ({
    tooltipId,
    tooltipContent,
    direction = 'right',
}) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLDivElement>(null);

    const toggleTooltip = () => {
        setIsTooltipVisible(!isTooltipVisible);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target as Node) &&
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node)
            ) {
                setIsTooltipVisible(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getTooltipPositionClasses = () => {
        switch (direction) {
            case 'left':
                return 'right-full mr-2';
            case 'right':
                return 'left-full ml-2 -translate-y-1/2';
            case 'top':
                return 'bottom-full mb-2 left-1/2 -translate-x-1/2';
            case 'bottom':
                return 'top-full mt-2 left-1/2 -translate-x-1/2';
            case 'top-left':
                return 'bottom-full mb-2 right-0';
            case 'top-right':
                return 'bottom-full mb-2 left-0';
            case 'bottom-left':
                return 'top-full mt-2 right-0';
            case 'bottom-right':
                return 'top-full mt-2 left-0';
            default:
                return 'top-full mt-2 left-0';
        }
    };

    const tooltipPositionClasses = getTooltipPositionClasses();

    return (
        <div className="relative inline-block">
            <div
                ref={buttonRef}
                className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 cursor-pointer transition-colors duration-200"
                onClick={toggleTooltip}
            >
                <FiInfo size={16} />
            </div>
            <div
                ref={tooltipRef}
                id={tooltipId}
                className={`absolute z-50 p-3 bg-neutral-800 text-neutral-200 text-sm rounded-md shadow-lg w-48 border border-neutral-700 transition-all duration-200 ease-in-out ${
                    isTooltipVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'
                } ${tooltipPositionClasses}`}
            >
                <TooltipContent content={tooltipContent} />
            </div>
        </div>
    );
};

export default InfoButton;